import { Calculator, FlaskConical, Sigma, TableProperties, Coins, BookOpen } from 'lucide-react';
import type { CeeFicheCalculation } from '@shared/schemas/cee-fiche-calculation';
import { CodeBlock } from '../ui/code-block';
import { cn } from '../../lib/cn';

interface FicheCalculationProps {
  /** Bloc de calcul stocké en JSONB sur la fiche CEE. */
  calculation: CeeFicheCalculation;
  /** Code de la fiche (ex. BAT-TH-122) — utilisé pour les labels. */
  code: string;
}

/**
 * Rendu pédagogique du calcul d'une fiche CEE, façon Opera Énergie :
 * décodage du code, formule, barème(s), exemple chiffré et conversion en €.
 *
 * Toutes les sections sont optionnelles : on n'affiche que ce qui est présent.
 */
export function FicheCalculation({ calculation, code }: FicheCalculationProps) {
  const { denomination, formula, tables, example, conversion } = calculation;
  const hasAnything =
    denomination || formula || (tables && tables.length > 0) || example || conversion;
  if (!hasAnything) return null;

  return (
    <div className="space-y-12">
      {denomination && <DenominationBlock code={code} denomination={denomination} />}
      {formula && <FormulaBlock code={code} formula={formula} />}
      {tables && tables.length > 0 && <TablesBlock tables={tables} />}
      {example && <ExampleBlock example={example} />}
      {conversion && <ConversionBlock conversion={conversion} />}
    </div>
  );
}

/* ────────────────────────── Dénomination ─────────────────────────── */

function DenominationBlock({
  code,
  denomination,
}: {
  code: string;
  denomination: NonNullable<CeeFicheCalculation['denomination']>;
}) {
  return (
    <section>
      <h2
        id="comprendre-le-code"
        className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-ink"
      >
        <BookOpen className="h-5 w-5 text-stone-400" />
        Comprendre la dénomination {code}
      </h2>
      <p className="mb-5 text-sm leading-relaxed text-stone-600">
        Les fiches CEE sont identifiées par un code unique structuré en trois parties&nbsp;:
      </p>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <DenominationCard
          tone="brand"
          tag={denomination.sector}
          meaning={denomination.sectorMeaning}
        />
        <DenominationCard
          tone="lavender"
          tag={denomination.type}
          meaning={denomination.typeMeaning}
        />
        <DenominationCard
          tone="amber"
          tag={denomination.number}
          meaning={denomination.numberMeaning}
        />
      </div>
    </section>
  );
}

type DenominationTone = 'brand' | 'lavender' | 'amber';

const DENOM_TONES: Record<DenominationTone, { bg: string; tag: string }> = {
  brand: {
    bg: 'bg-brand-50/60 border-brand-100',
    tag: 'bg-brand-100 text-brand-800 border-brand-200',
  },
  lavender: {
    bg: 'bg-lavender-50/60 border-lavender-100',
    tag: 'bg-lavender-100 text-lavender-800 border-lavender-200',
  },
  amber: {
    bg: 'bg-amber-50/60 border-amber-100',
    tag: 'bg-amber-100 text-amber-800 border-amber-200',
  },
};

function DenominationCard({
  tone,
  tag,
  meaning,
}: {
  tone: DenominationTone;
  tag: string;
  meaning: string;
}) {
  const t = DENOM_TONES[tone];
  return (
    <div className={cn('rounded-xl border p-4', t.bg)}>
      <span
        className={cn(
          'inline-block rounded-md border px-2 py-0.5 font-mono text-xs font-bold tracking-widest',
          t.tag,
        )}
      >
        {tag}
      </span>
      <p className="mt-3 text-sm leading-relaxed text-stone-700">{meaning}</p>
    </div>
  );
}

/* ───────────────────────────── Formule ───────────────────────────── */

function FormulaBlock({
  code,
  formula,
}: {
  code: string;
  formula: NonNullable<CeeFicheCalculation['formula']>;
}) {
  return (
    <section>
      <h2
        id="formule-calcul"
        className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-ink"
      >
        <Sigma className="h-5 w-5 text-stone-400" />
        Formule de calcul (kWh cumac)
      </h2>

      <CodeBlock label={`${code} — Formule officielle`} code={formula.text} showCopy />

      {formula.variables.length > 0 && (
        <dl className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {formula.variables.map((v) => (
            <div
              key={v.symbol}
              className="flex items-start gap-3 rounded-xl border border-stone-200 bg-white p-3"
            >
              <span className="mt-0.5 inline-flex h-7 min-w-7 items-center justify-center rounded-md bg-stone-900 px-2 font-mono text-xs font-bold text-white">
                {v.symbol}
              </span>
              <div className="min-w-0">
                <dt className="text-sm font-medium text-ink">{v.label}</dt>
                <dd className="mt-0.5 text-xs text-stone-500">
                  {[v.unit, v.source].filter(Boolean).join(' · ')}
                </dd>
              </div>
            </div>
          ))}
        </dl>
      )}

      {formula.note && (
        <p className="mt-4 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs leading-relaxed text-stone-500">
          {formula.note}
        </p>
      )}
    </section>
  );
}

/* ───────────────────────────── Barèmes ───────────────────────────── */

function TablesBlock({ tables }: { tables: NonNullable<CeeFicheCalculation['tables']> }) {
  return (
    <section>
      <h2
        id="bareme"
        className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-ink"
      >
        <TableProperties className="h-5 w-5 text-stone-400" />
        Barème officiel
      </h2>

      <div className="space-y-6">
        {tables.map((t, i) => (
          <div key={i}>
            <p className="mb-2 text-sm font-semibold text-ink">{t.title}</p>
            {t.caption && <p className="mb-3 text-xs text-stone-500">{t.caption}</p>}
            <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white shadow-subtle">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-left text-xs uppercase tracking-wider text-stone-500">
                  <tr>
                    {t.headers.map((h, hi) => (
                      <th
                        key={hi}
                        className={cn(
                          'px-4 py-3 font-semibold',
                          hi === 0 ? 'text-left' : 'text-right',
                        )}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {t.rows.map((row, ri) => (
                    <tr key={ri} className="transition-colors hover:bg-stone-50/70">
                      {row.map((cell, ci) => (
                        <td
                          key={ci}
                          className={cn(
                            'px-4 py-3 text-stone-700',
                            ci === 0
                              ? 'font-medium text-ink'
                              : 'text-right font-mono tabular-nums text-stone-700',
                          )}
                        >
                          {cell}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {t.footnote && (
              <p className="mt-2 text-xs italic leading-relaxed text-stone-500">{t.footnote}</p>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}

/* ───────────────────────────── Exemple ───────────────────────────── */

function ExampleBlock({ example }: { example: NonNullable<CeeFicheCalculation['example']> }) {
  return (
    <section>
      <h2
        id="exemple-calcul"
        className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-ink"
      >
        <FlaskConical className="h-5 w-5 text-stone-400" />
        Exemple chiffré
      </h2>

      <div className="overflow-hidden rounded-2xl border border-brand-100 bg-brand-50/40">
        <div className="border-b border-brand-100 bg-brand-50/60 px-5 py-3">
          <p className="text-sm font-semibold text-ink">{example.title}</p>
        </div>

        {/* Inputs */}
        <div className="px-5 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">
            Données d'entrée
          </p>
          <div className="mt-2 flex flex-wrap gap-2">
            {example.inputs.map((input, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-2 rounded-lg border border-stone-200 bg-white px-3 py-1.5 text-xs"
              >
                <span className="text-stone-500">{input.label}</span>
                <span className="font-semibold text-ink">{input.value}</span>
              </span>
            ))}
          </div>
        </div>

        {/* Steps */}
        <div className="px-5 pb-5 pt-4">
          <p className="text-xs font-semibold uppercase tracking-wider text-stone-400">Calcul</p>
          <ol className="mt-2 space-y-2">
            {example.steps.map((step, i) => (
              <li
                key={i}
                className={cn(
                  'flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 rounded-lg px-3 py-2.5',
                  step.highlight
                    ? 'border border-brand-300 bg-brand-100/70 shadow-subtle'
                    : 'border border-stone-100 bg-white',
                )}
              >
                <span
                  className={cn(
                    'text-sm',
                    step.highlight ? 'font-semibold text-ink' : 'text-stone-600',
                  )}
                >
                  {step.label}
                </span>
                <span
                  className={cn(
                    'font-mono text-sm tabular-nums',
                    step.highlight ? 'font-bold text-brand-800' : 'text-ink',
                  )}
                >
                  {step.value}
                </span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────── Conversion € ────────────────────────── */

function ConversionBlock({
  conversion,
}: {
  conversion: NonNullable<CeeFicheCalculation['conversion']>;
}) {
  return (
    <section>
      <h2
        id="conversion-euros"
        className="mb-4 flex items-center gap-2 font-display text-xl font-bold text-ink"
      >
        <Coins className="h-5 w-5 text-stone-400" />
        Conversion en euros
      </h2>

      <div className="rounded-2xl border border-stone-200 bg-white p-5">
        <p className="text-sm leading-relaxed text-stone-600">
          Le montant final de la prime CEE dépend du tarif du marché des primes, qui fluctue selon
          l'offre et la demande.
        </p>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-xl border border-stone-200 bg-stone-50 p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-stone-400">Prix bas</p>
            <p className="mt-1 font-mono text-2xl font-bold text-ink">
              {conversion.minEurPerMwh.toFixed(0)}
              <span className="ml-1 text-sm font-normal text-stone-500">€/MWh cumac</span>
            </p>
          </div>
          <div className="rounded-xl border border-brand-200 bg-brand-50/60 p-4">
            <p className="text-xs font-medium uppercase tracking-wider text-brand-700">Prix haut</p>
            <p className="mt-1 font-mono text-2xl font-bold text-brand-800">
              {conversion.maxEurPerMwh.toFixed(0)}
              <span className="ml-1 text-sm font-normal text-brand-700/70">€/MWh cumac</span>
            </p>
          </div>
        </div>

        {conversion.note && (
          <p className="mt-4 flex items-start gap-2 rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs leading-relaxed text-stone-500">
            <Calculator className="mt-0.5 h-3.5 w-3.5 shrink-0 text-stone-400" />
            <span>{conversion.note}</span>
          </p>
        )}
      </div>
    </section>
  );
}
